import React, { useState, useEffect, useRef } from 'react';
import { tradingAPI } from '../../../../services/tradingAPI';
import api from '../../../../services/api';
import '../../../../css/trading.css';

const thresholds = {
  pe_ratio_max: 15.0,
  pb_ratio_max: 2.0,
  debt_to_equity_max: 0.5,
  roe_min: 20.0,
};

const StockSearch = () => {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [stockData, setStockData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState(null);
  const debounceRef = useRef(null);
  const dropdownRef = useRef(null);

  // Autocomplete search as user types
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (query.length < 2) { setSuggestions([]); setShowDropdown(false); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        setSearching(true);
        const res = await tradingAPI.searchStocks(query);
        setSuggestions(res.data.results || []);
        setShowDropdown(true);
      } catch {
        setSuggestions([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query]);

  // Close dropdown on outside click
  useEffect(() => {
    const h = (e) => { if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setShowDropdown(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const selectStock = async (symbol, name) => {
    setQuery(`${symbol} — ${name}`);
    setShowDropdown(false);
    setSuggestions([]);
    setLoading(true);
    setError(null);
    setStockData(null);
    try {
      const res = await api.get(`/trading/stock-lookup/${symbol}/`);
      setStockData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch stock data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const ticker = query.split('—')[0].trim().toUpperCase();
    if (!ticker) return;
    selectStock(ticker, '');
  };

  const check = (value, threshold, isMax = true) => {
    if (value === null || value === undefined) return 'unknown';
    return isMax ? (value <= threshold ? 'pass' : 'fail') : (value >= threshold ? 'pass' : 'fail');
  };

  const fmt = (v, d = 2) => (v === null || v === undefined ? 'N/A' : parseFloat(v).toFixed(d));

  const allPass = stockData && ['pe_ratio','pb_ratio','debt_to_equity'].every(k => check(stockData[k], thresholds[`${k}_max`]) === 'pass')
    && check(stockData.roe, thresholds.roe_min, false) === 'pass';

  return (
    <div className="stock-search-tab">
      <div className="search-header">
        <h2>Stock Search</h2>
        <p>Search by ticker symbol or company name to evaluate against your trading thresholds</p>
      </div>

      <form onSubmit={handleSubmit} className="search-form">
        <div className="search-input-group" ref={dropdownRef} style={{ position: 'relative' }}>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Ticker or company name (e.g. AAPL or Apple)"
            className="search-input"
            autoComplete="off"
          />
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Loading…' : 'Search'}
          </button>

          {showDropdown && suggestions.length > 0 && (
            <ul className="stock-dropdown" style={{
              position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200,
              background: '#1a1a2e', border: '1px solid #333', borderRadius: '6px',
              listStyle: 'none', margin: 0, padding: 0, maxHeight: '260px', overflowY: 'auto'
            }}>
              {suggestions.map((s) => (
                <li
                  key={s.symbol}
                  onClick={() => selectStock(s.symbol, s.name)}
                  style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid #222' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#252545'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <strong style={{ color: '#a78bfa' }}>{s.symbol}</strong>
                  <span style={{ marginLeft: 8, color: '#ccc', fontSize: '0.9em' }}>{s.name}</span>
                  {s.exchange && <span style={{ marginLeft: 8, color: '#666', fontSize: '0.8em' }}>{s.exchange}</span>}
                </li>
              ))}
            </ul>
          )}
          {searching && <div style={{ position: 'absolute', top: '100%', padding: '8px 14px', color: '#888', fontSize: '0.85em' }}>Searching…</div>}
        </div>
      </form>

      {error && <div className="error-message"><p>{error}</p></div>}

      {stockData && (
        <div className="stock-results">
          <div className="stock-header">
            <h3 className="stock-title">{stockData.symbol} — {stockData.name}</h3>
            <div className="stock-price">${fmt(stockData.current_price)}</div>
          </div>

          <div className="threshold-analysis">
            <h4>Threshold Analysis</h4>
            <div className="threshold-grid">
              {[
                { label: 'P/E Ratio', key: 'pe_ratio', tKey: 'pe_ratio_max', isMax: true, suffix: '' },
                { label: 'P/B Ratio', key: 'pb_ratio', tKey: 'pb_ratio_max', isMax: true, suffix: '' },
                { label: 'Debt/Equity', key: 'debt_to_equity', tKey: 'debt_to_equity_max', isMax: true, suffix: '' },
                { label: 'ROE', key: 'roe', tKey: 'roe_min', isMax: false, suffix: '%' },
              ].map(({ label, key, tKey, isMax, suffix }) => (
                <div key={key} className={`threshold-item ${check(stockData[key], thresholds[tKey], isMax)}`}>
                  <div className="threshold-label">{label}</div>
                  <div className="threshold-value">
                    {fmt(stockData[key])}{suffix}
                    <span className="threshold-target">{isMax ? 'Max' : 'Min'}: {thresholds[tKey]}{suffix}</span>
                  </div>
                  <div className="threshold-status">
                    {check(stockData[key], thresholds[tKey], isMax) === 'pass' ? '✓ Pass' : '✗ Fail'}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="additional-metrics">
            <h4>Additional Metrics</h4>
            <div className="metrics-grid">
              <div className="metric-item"><div className="metric-label">Market Cap</div><div className="metric-value">{stockData.market_cap || 'N/A'}</div></div>
              <div className="metric-item"><div className="metric-label">Earnings</div><div className="metric-value">{fmt(stockData.earnings)}</div></div>
              <div className="metric-item"><div className="metric-label">Free Cash Flow</div><div className="metric-value">{fmt(stockData.fcf)}</div></div>
              <div className="metric-item"><div className="metric-label">Dividend Yield</div><div className="metric-value">{fmt(stockData.dividend_yield)}%</div></div>
            </div>
          </div>

          <div className="verdict-section">
            <h4>Strategy Verdict</h4>
            <div className={`verdict ${allPass ? 'pass' : 'fail'}`}>
              {allPass ? '✓ Meets all threshold criteria' : '✗ Does not meet all threshold criteria'}
            </div>
          </div>
        </div>
      )}

      {!stockData && !loading && !error && (
        <div className="search-instructions">
          <h3>How to use Stock Search</h3>
          <ul>
            <li>Type a ticker (e.g. AAPL) or a company name (e.g. Thomson Reuters) — autocomplete will appear</li>
            <li>Select a result or press Search to evaluate the stock</li>
            <li>View real-time fundamentals vs. your strategy thresholds</li>
          </ul>
        </div>
      )}
    </div>
  );
};

export default StockSearch;
